export type PublicIndustryIconId =
  | "agency"
  | "agriculture"
  | "automotive"
  | "bakery"
  | "bar"
  | "beauty"
  | "bookstore"
  | "carpentry"
  | "construction"
  | "courier"
  | "crafts"
  | "electronics"
  | "fashion"
  | "fastFood"
  | "fitness"
  | "furniture"
  | "garden"
  | "gastro"
  | "hair"
  | "hotel"
  | "library"
  | "optical"
  | "pharmacy"
  | "plumbing"
  | "realEstate"
  | "spa"
  | "tattoo"
  | "tyreService"
  | "toy"
  | "winery";

export type PresetIndustryPresentation = {
  glow: string;
  icon: PublicIndustryIconId;
  primary: string;
  publicIndustrySlug: string;
  secondary: string;
};

const presentationStyles = {
  amber: { primary: "#f59e0b", secondary: "#f97316", glow: "rgba(245, 158, 11, .24)" },
  blue: { primary: "#3b82f6", secondary: "#4f46e5", glow: "rgba(59, 130, 246, .24)" },
  cyan: { primary: "#06b6d4", secondary: "#0284c7", glow: "rgba(6, 182, 212, .24)" },
  emerald: { primary: "#10b981", secondary: "#0d9488", glow: "rgba(16, 185, 129, .24)" },
  indigo: { primary: "#6366f1", secondary: "#2563eb", glow: "rgba(99, 102, 241, .24)" },
  lime: { primary: "#84cc16", secondary: "#10b981", glow: "rgba(132, 204, 22, .24)" },
  orange: { primary: "#f97316", secondary: "#dc2626", glow: "rgba(249, 115, 22, .24)" },
  pink: { primary: "#ec4899", secondary: "#e11d48", glow: "rgba(236, 72, 153, .24)" },
  purple: { primary: "#a855f7", secondary: "#4f46e5", glow: "rgba(168, 85, 247, .24)" },
  slate: { primary: "#64748b", secondary: "#0891b2", glow: "rgba(100, 116, 139, .24)" },
  violet: { primary: "#8b5cf6", secondary: "#d946ef", glow: "rgba(139, 92, 246, .24)" },
} as const;

/** Every backend industry preset has one reviewed public presentation counterpart. */
export const exactPublicIndustryPresentationByPresetCode = {
  "agricultural-store": {
    icon: "agriculture", publicIndustrySlug: "zemedelske-potreby", ...presentationStyles.emerald,
  },
  "arts-and-crafts-shop": {
    icon: "crafts", publicIndustrySlug: "vytvarne-a-kreativni-potreby", ...presentationStyles.purple,
  },
  "auto-service": {
    icon: "automotive", publicIndustrySlug: "autoservisy", ...presentationStyles.slate,
  },
  bakery: {
    icon: "bakery", publicIndustrySlug: "pekarny-a-cukrarny", ...presentationStyles.orange,
  },
  "bar-and-pub": {
    icon: "bar", publicIndustrySlug: "bary-a-puby", ...presentationStyles.orange,
  },
  "beauty-parlor": {
    icon: "beauty", publicIndustrySlug: "kosmeticke-salony", ...presentationStyles.cyan,
  },
  bookstore: {
    icon: "bookstore", publicIndustrySlug: "knihkupectvi-a-papirnictvi", ...presentationStyles.pink,
  },
  carpenter: {
    icon: "carpentry", publicIndustrySlug: "truhlari-a-tesarstvi", ...presentationStyles.amber,
  },
  "clothing-store": {
    icon: "fashion", publicIndustrySlug: "moda-a-obuv", ...presentationStyles.pink,
  },
  "cosmetics-store": {
    icon: "beauty", publicIndustrySlug: "drogerie-a-kosmetika", ...presentationStyles.pink,
  },
  courier: {
    icon: "courier", publicIndustrySlug: "kuryri", ...presentationStyles.indigo,
  },
  "electronics-store": {
    icon: "electronics", publicIndustrySlug: "elektronika-a-elektro", ...presentationStyles.pink,
  },
  "eyewear-store": {
    icon: "optical", publicIndustrySlug: "optiky", ...presentationStyles.cyan,
  },
  "fast-food": {
    icon: "fastFood", publicIndustrySlug: "fast-food-a-rozvoz", ...presentationStyles.orange,
  },
  "fitness-center": {
    icon: "fitness", publicIndustrySlug: "fitness-a-wellness", ...presentationStyles.lime,
  },
  "furniture-store": {
    icon: "furniture", publicIndustrySlug: "nabytek-a-bydleni", ...presentationStyles.pink,
  },
  gardening: {
    icon: "garden", publicIndustrySlug: "zahradnici-a-krajinari", ...presentationStyles.amber,
  },
  "hair-salon": {
    icon: "hair", publicIndustrySlug: "kadernictvi-a-barber-shopy", ...presentationStyles.cyan,
  },
  hotel: {
    icon: "hotel", publicIndustrySlug: "hotely-a-penziony", ...presentationStyles.orange,
  },
  library: {
    icon: "library", publicIndustrySlug: "knihovny", ...presentationStyles.lime,
  },
  "marketing-agency": {
    icon: "agency", publicIndustrySlug: "agentury-a-studia", ...presentationStyles.violet,
  },
  "personal-trainer": {
    icon: "fitness", publicIndustrySlug: "osobni-treneri", ...presentationStyles.lime,
  },
  pharmacy: {
    icon: "pharmacy", publicIndustrySlug: "lekarny", ...presentationStyles.blue,
  },
  plumber: {
    icon: "plumbing", publicIndustrySlug: "instalateri-a-topenari", ...presentationStyles.amber,
  },
  "real-estate-agency": {
    icon: "realEstate", publicIndustrySlug: "realitni-kancelare", ...presentationStyles.blue,
  },
  restaurant: {
    icon: "gastro", publicIndustrySlug: "restaurace-a-gastro", ...presentationStyles.orange,
  },
  "spa-resort": {
    icon: "spa", publicIndustrySlug: "lazenske-resorty", ...presentationStyles.emerald,
  },
  "tattoo-shop": {
    icon: "tattoo", publicIndustrySlug: "tetovaci-studia", ...presentationStyles.violet,
  },
  "tire-service": {
    icon: "tyreService", publicIndustrySlug: "pneuservisy", ...presentationStyles.slate,
  },
  "toy-store": {
    icon: "toy", publicIndustrySlug: "hrackarstvi", ...presentationStyles.indigo,
  },
  tradesperson: {
    icon: "construction", publicIndustrySlug: "stavebnictvi-a-remesla", ...presentationStyles.amber,
  },
  "wine-shop": {
    icon: "winery", publicIndustrySlug: "vinarstvi-a-vinoteky", ...presentationStyles.orange,
  },
} as const satisfies Record<string, PresetIndustryPresentation>;

export function getPresetIndustryPresentation(
  presetCode: string,
): PresetIndustryPresentation | undefined {
  return Object.prototype.hasOwnProperty.call(
    exactPublicIndustryPresentationByPresetCode,
    presetCode,
  )
    ? exactPublicIndustryPresentationByPresetCode[
        presetCode as keyof typeof exactPublicIndustryPresentationByPresetCode
      ]
    : undefined;
}

export function getPresetCodeForPublicIndustrySlug(
  publicIndustrySlug: string,
): string | undefined {
  return Object.entries(exactPublicIndustryPresentationByPresetCode).find(
    ([, presentation]) => presentation.publicIndustrySlug === publicIndustrySlug,
  )?.[0];
}
